import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, AlertCircle, CheckCircle2, PauseCircle } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Tenant } from '@/types';

export function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isSuperAdmin = api.isSuperAdmin();

  useEffect(() => {
    api.listTenants()
      .then(setTenants)
      .catch((e) => setError(e instanceof ApiClientError ? e.message : 'Failed to load tenants'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Tenants</h2>
          <p className="text-gray-500 mt-1 text-sm">Manage organisations and their usage quotas</p>
        </div>
        {isSuperAdmin && (
          <Link to="/tenants/new" className="btn-primary">
            <Plus size={16} className="mr-2" />
            Add Tenant
          </Link>
        )}
      </div>

      {loading && (
        <div className="text-sm text-gray-500 py-12 text-center">Loading…</div>
      )}

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg text-red-800 text-sm">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {!loading && !error && tenants.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users size={40} className="text-gray-300 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 mb-1">No tenants yet</p>
          <p className="text-sm text-gray-500 mb-4">Add your first organisation to start issuing widget tokens.</p>
          {isSuperAdmin && (
            <Link to="/tenants/new" className="btn-primary">
              <Plus size={15} className="mr-2" />
              Add Tenant
            </Link>
          )}
        </div>
      )}

      {tenants.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3 text-left">Tenant</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-right">Users</th>
                <th className="px-5 py-3 text-right">Max / Day</th>
                <th className="px-5 py-3 text-right">Max / Month</th>
                <th className="px-5 py-3 text-right">Quota / User</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tenants.map((t) => {
                const quotaPerUser = t.user_count
                  ? Math.floor(t.max_requests_per_day / t.user_count)
                  : t.max_requests_per_day;
                return (
                  <tr key={t.tenant_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-900">{t.name}</div>
                      <div className="text-xs text-gray-500 font-mono mt-0.5">{t.tenant_id}</div>
                    </td>
                    <td className="px-5 py-4">
                      <StatusBadge status={t.status} />
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums">
                      <span className={t.user_count === t.max_users ? 'text-amber-600 font-semibold' : ''}>
                        {t.user_count ?? 0}
                      </span>
                      <span className="text-gray-400"> / {t.max_users}</span>
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums text-gray-700">
                      {t.max_requests_per_day.toLocaleString()}
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums text-gray-700">
                      {t.max_requests_per_month.toLocaleString()}
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums text-gray-700">
                      {quotaPerUser.toLocaleString()}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Link
                        to={`/tenants/${t.tenant_id}`}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Tenant['status'] }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
        <CheckCircle2 size={11} />
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
      <PauseCircle size={11} />
      Suspended
    </span>
  );
}
